import { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

import { Badge, Box, ClickAwayListener, Divider, IconButton, List, ListItemButton, ListItemText, Paper, Popper, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { Bell, CheckCheck, FileWarning, Info, Printer, QrCode } from 'lucide-react';

import { fetchNotifications, markNotificationRead, markAllRead } from '../../../../Redux/slices/notificationSlice';
import { selectSelectedCompanyId } from '../../../../Redux/slices/ownerSlice';
import { selectIsOwner } from '../../../../Redux/slices/authSlice';

const ICONS = {
  NewClaim: { Icon: FileWarning, color: 'error.main', bg: 'error.lighter' },
  ClaimUpdated: { Icon: FileWarning, color: 'warning.dark', bg: 'warning.lighter' },
  QRGenerated: { Icon: QrCode, color: 'primary.main', bg: 'primary.lighter' },
  PrintDone: { Icon: Printer, color: 'success.dark', bg: 'success.lighter' }
};

const NotifIcon = ({ type }) => {
  const { Icon, color, bg } = ICONS[type] || { Icon: Info, color: 'info.dark', bg: 'info.lighter' };
  return (
    <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: bg, color, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
      <Icon size={16} strokeWidth={1.7} />
    </Box>
  );
};

// ==============================|| HEADER - NOTIFICATIONS ||============================== //

export default function Notification() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const { items, unreadCount, loading } = useSelector((s) => s.notification);
  const selectedCompanyId = useSelector(selectSelectedCompanyId);
  const isOwner = useSelector(selectIsOwner);

  useEffect(() => {
    dispatch(fetchNotifications({ pageSize: 20 }));
  }, [dispatch, isOwner ? selectedCompanyId : null]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = items.slice(0, 8);

  const openItem = (n) => {
    if (!n.isRead) dispatch(markNotificationRead(n.notificationId));
    if (n.actionUrl) { setOpen(false); navigate(n.actionUrl); }
  };

  return (
    <Box sx={{ flexShrink: 0 }}>
      <Tooltip title="Notifications">
        <IconButton ref={anchorRef} size="small" onClick={() => setOpen((v) => !v)} sx={{ color: 'grey.600', bgcolor: open ? 'grey.100' : 'transparent' }}>
          <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error" max={99} sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}>
            <Bell size={19} strokeWidth={1.6} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popper placement="bottom-end" open={open} anchorEl={anchorRef.current} disablePortal modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]} sx={{ zIndex: 1300 }}>
        <ClickAwayListener onClickAway={(e) => { if (!anchorRef.current?.contains(e.target)) setOpen(false); }}>
          <Paper variant="outlined" sx={{ width: 380, maxWidth: 'calc(100vw - 24px)', boxShadow: '0 10px 28px rgba(16,24,40,0.12)', overflow: 'hidden' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1">Notifications</Typography>
              {unreadCount > 0 && (
                <Tooltip title="Mark all as read">
                  <IconButton size="small" onClick={() => dispatch(markAllRead())} sx={{ color: 'primary.main' }}><CheckCheck size={16} /></IconButton>
                </Tooltip>
              )}
            </Stack>
            <List disablePadding sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {loading && shown.length === 0 && [1, 2, 3].map((i) => (
                <Stack key={i} direction="row" spacing={1.5} sx={{ p: 2 }}><Skeleton variant="rounded" width={32} height={32} /><Box sx={{ flex: 1 }}><Skeleton width="80%" /><Skeleton width="40%" /></Box></Stack>
              ))}
              {!loading && shown.length === 0 && (
                <Box sx={{ py: 5, textAlign: 'center', color: 'grey.500' }}>
                  <Bell size={26} strokeWidth={1.3} />
                  <Typography variant="body2" sx={{ mt: 1 }}>You are all caught up</Typography>
                </Box>
              )}
              {shown.map((n, i) => (
                <Box key={n.notificationId}>
                  <ListItemButton onClick={() => openItem(n)} sx={{ alignItems: 'flex-start', gap: 1.5, py: 1.25, bgcolor: n.isRead ? 'transparent' : 'primary.lighter' }}>
                    <NotifIcon type={n.type} />
                    <ListItemText
                      primary={n.message}
                      secondary={n.createdAt ? formatDistanceToNow(new Date(n.createdAt), { addSuffix: true }) : ''}
                      primaryTypographyProps={{ fontSize: 13, fontWeight: n.isRead ? 400 : 600, lineHeight: 1.4 }}
                      secondaryTypographyProps={{ fontSize: 11.5 }}
                      sx={{ m: 0 }}
                    />
                  </ListItemButton>
                  {i < shown.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}
